import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import * as Dialog from "@radix-ui/react-dialog";
import { api } from "../lib/api";
import { useEntitySections, getFieldLabel, type EntityWithChanges } from "../lib/types";
import { ChangelogTimeline } from "../components/ChangelogTimeline";
import { SectionCard } from "../components/ui/SectionCard";
import { Button } from "../components/ui/Button";
import i18n from "../i18n";

interface RestoreErrorDetail {
  error?: string;
  conflicting_id?: string;
}

function SkeletonView(): JSX.Element {
  return (
    <div className="max-w-4xl mx-auto space-y-4">
      <div className="skeleton h-8 w-40" />
      <div className="skeleton h-28" />
      <div className="skeleton h-48" />
      <div className="skeleton h-48" />
    </div>
  );
}

export default function AdminDeletedEntityView(): JSX.Element {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation(["admin", "entities", "common", "audit"]);
  const [restoreOpen, setRestoreOpen] = useState(false);

  const { data: entity, isLoading, isError } = useQuery<EntityWithChanges>({
    queryKey: ["deleted-entity", id],
    queryFn: async () => {
      const res = await api.get<EntityWithChanges>(`/admin/deleted-entities/${id}`);
      return res.data;
    },
    enabled: Boolean(id),
  });

  const restoreMutation = useMutation({
    mutationFn: () => api.post(`/admin/entities/${id}/restore`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["entities"] });
      void queryClient.invalidateQueries({ queryKey: ["entities-all"] });
      void queryClient.invalidateQueries({ queryKey: ["renewals"] });
      void queryClient.invalidateQueries({ queryKey: ["deleted-entities"] });
      void queryClient.invalidateQueries({ queryKey: ["deleted-entity", id] });
      toast.success(t("admin:restore_success"));
      setRestoreOpen(false);
      navigate(`/entities/${id}`);
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: RestoreErrorDetail } })?.response?.data;
      if (detail?.error === "name_already_taken") {
        toast.error(
          t("admin:restore_name_clash", { conflicting_id: detail.conflicting_id ?? "?" }),
          { duration: 8000 },
        );
      } else if (detail?.error === "not_deleted") {
        toast.success(t("admin:already_restored"));
        void queryClient.invalidateQueries({ queryKey: ["deleted-entities"] });
        navigate(`/entities/${id}`);
      } else {
        toast.error(t("admin:restore_error"));
      }
      setRestoreOpen(false);
    },
  });

  // ALL hooks must be called before any early return — Rules of Hooks.
  const entitySections = useEntitySections();

  if (isLoading) return <SkeletonView />;

  if (isError || !entity) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-surface border border-border rounded-[11px] shadow-card p-6 text-center">
          <h1 className="font-display font-semibold text-[15px] text-text">
            {t("common:not_found")}
          </h1>
          <Link
            to="/admin/deleted-entities"
            className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] text-muted hover:text-text font-medium"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
            {t("admin:back_to_deleted")}
          </Link>
        </div>
      </div>
    );
  }

  // Derive deletedAt from the entity_deleted ChangeLog row as fallback.
  const deletedChange = entity.changes?.find((c) => c.field === "entity_deleted");
  const deletedAtRaw = entity.deletedAt ?? deletedChange?.createdAt;
  const deletedOnDate = deletedAtRaw
    ? new Date(deletedAtRaw).toLocaleDateString(i18n.language)
    : "—";
  const deletedActor = deletedChange?.user?.name ?? deletedChange?.comment ?? "—";

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back link */}
      <Link
        to="/admin/deleted-entities"
        className="inline-flex items-center gap-1.5 text-[12.5px] text-muted hover:text-text font-medium mb-3 min-h-[44px] sm:min-h-0"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        {t("admin:back_to_deleted")}
      </Link>

      {/* Header card */}
      <div className="bg-surface border border-border rounded-[11px] shadow-card p-[15px_16px] mb-5 flex flex-wrap items-start gap-3">
        <span aria-hidden className="text-3xl">{entity.country?.flag ?? "🌐"}</span>
        <div className="min-w-0 flex-1">
          <h1 className="font-display font-semibold text-[24px] leading-[1.1] text-text">
            {entity.name}
          </h1>
          <p className="text-[13px] text-muted mt-[5px]">
            {entity.country?.name ?? "—"} · {entity.legalForm ?? "—"} · {entity.region}
          </p>
        </div>
        {/* Deleted badge */}
        <span className="inline-flex items-center px-[9px] py-[2px] rounded-full text-[11.5px] font-semibold border bg-red-soft text-red-text border-[#fcc8c2]">
          {t("admin:deleted_on_by", { date: deletedOnDate, actor: deletedActor })}
        </span>
      </div>

      <div className="space-y-5">
        {/* Entity sections — read-only, using SectionCard */}
        {entitySections.map((s) => (
          <SectionCard key={s.title} title={s.title}>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              {s.fields.map((f) => {
                const val = (entity[f] as string | null) ?? null;
                return (
                  <div
                    key={f as string}
                    className="flex items-center gap-[14px] py-[8px] border-b border-border-2 last:border-b-0"
                  >
                    <dt className="w-[148px] shrink-0 text-[13.5px] text-label">
                      {getFieldLabel(f as string)}
                    </dt>
                    <dd className="text-[14px] font-semibold text-text min-w-0">
                      {val ? val : <span className="text-dim font-normal">—</span>}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </SectionCard>
        ))}

        {/* Restore card — shown before the audit log */}
        <SectionCard title={t("admin:restore_section_title")}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 py-[5px]">
            <div>
              <p className="text-[14px] font-semibold text-text">{t("admin:restore_entity_label")}</p>
              <p className="text-[12.5px] text-muted mt-0.5">{t("admin:restore_entity_description")}</p>
            </div>
            <Dialog.Root open={restoreOpen} onOpenChange={setRestoreOpen}>
              <Dialog.Trigger asChild>
                <Button variant="secondary" type="button" className="shrink-0">
                  {t("common:restore")}
                </Button>
              </Dialog.Trigger>
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
                <Dialog.Content
                  className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50
                             bg-surface rounded-[11px] border border-border shadow-pop
                             p-6 w-[min(92vw,380px)] focus:outline-none"
                  aria-describedby="restore-preview-desc"
                >
                  <Dialog.Title className="font-display font-semibold text-[15px] tracking-[-0.01em] text-text mb-2">
                    {t("admin:restore_title", { name: entity.name })}
                  </Dialog.Title>
                  <Dialog.Description id="restore-preview-desc" className="text-[13px] text-muted mb-5">
                    {t("admin:restore_desc")}
                  </Dialog.Description>
                  <div className="flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      type="button"
                      onClick={() => setRestoreOpen(false)}
                    >
                      {t("common:cancel")}
                    </Button>
                    <Button
                      variant="primary"
                      type="button"
                      onClick={() => restoreMutation.mutate()}
                      disabled={restoreMutation.isPending}
                    >
                      {restoreMutation.isPending ? t("admin:restoring_label") : t("common:restore")}
                    </Button>
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          </div>
        </SectionCard>

        {/* Audit log */}
        {entity.changes && entity.changes.length > 0 && (
          <SectionCard title={t("audit:page_title")} bodyClassName="p-0">
            <ChangelogTimeline changes={entity.changes} />
          </SectionCard>
        )}
      </div>
    </div>
  );
}
